'use client';

import { useState } from 'react';
import type { GameId } from '@vuganawe/types';
import type { WordPair } from '@/content/games';
import { useT } from '@/i18n/client';
import { submitGameResult } from '@/lib/api/public';
import { recordGuestGameRound } from '@/lib/gamification';
import { getLearnerToken } from '@/lib/learnerSession';
import { SentenceBuilder } from './SentenceBuilder';
import { SoundToggle } from './SoundToggle';
import type { GameFinish } from './WordMatch';
import { WordGuess } from './WordGuess';
import { WordMatch } from './WordMatch';
import { WordRush } from './WordRush';

/**
 * A practice-game launcher scoped to a single lesson's own content.
 *
 * This is where lesson-linked games are "managed": rather than a separate
 * admin screen, the games draw their word bank from the lesson's `vocabulary`
 * and `examples`, so whatever an admin authors on the lesson automatically
 * becomes playable practice. Games needing more data than the lesson provides
 * are simply hidden. The standalone /games hub still uses the global bank.
 */
export function LessonPracticeGame({
  pairs,
  sentences,
}: {
  pairs: WordPair[];
  sentences: string[];
}) {
  const t = useT();
  const [active, setActive] = useState<GameId | null>(null);
  const [lastXp, setLastXp] = useState<number | null>(null);

  const options: { id: GameId; icon: string; enabled: boolean }[] = [
    { id: 'word-match', icon: '🃏', enabled: pairs.length >= 3 },
    { id: 'word-rush', icon: '⚡', enabled: pairs.length >= 4 },
    { id: 'word-guess', icon: '🔤', enabled: pairs.length >= 3 },
    { id: 'sentence-builder', icon: '🧩', enabled: sentences.length >= 2 },
  ];
  const available = options.filter((option) => option.enabled);
  if (available.length === 0) return null;

  const keyOf: Record<GameId, string> = {
    'word-match': 'wordMatch',
    'word-rush': 'wordRush',
    'sentence-builder': 'sentenceBuilder',
    'word-guess': 'wordGuess',
  };

  function finish(game: GameId, result: GameFinish) {
    const token = getLearnerToken();
    if (!token) {
      const local = recordGuestGameRound({
        game,
        correct: result.correct,
        total: result.total,
        score: result.score,
      });
      setLastXp(local.xpAwarded);
      return;
    }
    submitGameResult(token, game, result.correct, result.total, result.score)
      .then((res) => setLastXp(res.xpAwarded))
      .catch(() => {
        /* offline — the round still played, XP just isn't recorded */
      });
  }

  if (active) {
    const onFinish = (result: GameFinish) => finish(active, result);
    return (
      <div className="vn-lessongame">
        <div className="vn-lessongame__head">
          <button
            type="button"
            className="vn-btn vn-btn--ghost vn-btn--small"
            onClick={() => {
              setActive(null);
              setLastXp(null);
            }}
          >
            ‹ {t('lesson.practiceGame')}
          </button>
          {lastXp !== null ? <span className="vn-games__xp">+{lastXp} XP</span> : <span />}
          <SoundToggle />
        </div>
        {active === 'word-match' ? <WordMatch onFinish={onFinish} pairs={pairs} /> : null}
        {active === 'word-rush' ? <WordRush onFinish={onFinish} pairs={pairs} /> : null}
        {active === 'word-guess' ? <WordGuess onFinish={onFinish} pairs={pairs} /> : null}
        {active === 'sentence-builder' ? (
          <SentenceBuilder onFinish={onFinish} bank={sentences} />
        ) : null}
      </div>
    );
  }

  return (
    <div className="vn-lessongame">
      <p className="vn-lessongame__intro">{t('lesson.launchGame')}</p>
      <div className="vn-lessongame__grid">
        {available.map((option) => (
          <button
            key={option.id}
            type="button"
            className="vn-lessongame__card"
            onClick={() => {
              setActive(option.id);
              setLastXp(null);
            }}
          >
            <span aria-hidden="true" className="vn-lessongame__icon">
              {option.icon}
            </span>
            <span className="vn-lessongame__title">{t(`games.${keyOf[option.id]}.title`)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
